import React, { useState, useContext, useEffect, useCallback } from 'react';
import { AuthContext } from '../AuthContext';
import axiosInstance from '../axiosConfig';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import styles from './Login.module.css';

function OTPVerification() {
    const [otp, setOtp] = useState('');
    const { verifyOtp, setOtpRequired, authTokens, logoutUser, user, telegramOtpEnabled } = useContext(AuthContext);
    const [resendTimer, setResendTimer] = useState(60);
    const [resendMessage, setResendMessage] = useState('');
    const [canResend, setCanResend] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isResending, setIsResending] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = useCallback(async (otpCode) => {
        try {
            const success = await verifyOtp(otpCode);
            if (!success) {
                setErrorMessage('Неверный код или он устарел. Попробуйте снова.');
            } else {
                setOtpRequired(false);
                navigate(user?.selectedServiceCenter ? '/home' : '/select-service-center');
            }
        } catch {
            setErrorMessage('Ошибка при верификации. Попробуйте снова.');
        }
    }, [verifyOtp, setOtpRequired, user, navigate]);

    useEffect(() => { if (!authTokens) navigate('/login'); }, [authTokens, navigate]);

    useEffect(() => {
        if (!telegramOtpEnabled) {
            setOtpRequired(false);
            navigate('/');
        }
    }, [telegramOtpEnabled, setOtpRequired, navigate]);

    useEffect(() => {
        const lastSentTime = localStorage.getItem('otpLastSentTime');
        if (lastSentTime) {
            const elapsed = Math.floor((Date.now() - parseInt(lastSentTime)) / 1000);
            if (elapsed < 60) { setResendTimer(60 - elapsed); setCanResend(false); }
            else setCanResend(true);
        } else setCanResend(true);
    }, []);

    useEffect(() => {
        if (resendTimer > 0) {
            const t = setTimeout(() => setResendTimer(v => v - 1), 1000);
            return () => clearTimeout(t);
        } else setCanResend(true);
    }, [resendTimer]);

    useEffect(() => { if (otp.length === 6) handleSubmit(otp); }, [otp, handleSubmit]);

    const handleResend = async () => {
        setErrorMessage(''); setIsResending(true);
        if (!authTokens?.access) { logoutUser(); return; }
        try {
            const res = await axiosInstance.post('/api/send-otp/', {}, {
                headers: { Authorization: `Bearer ${authTokens.access}` }
            });
            if (res.data.success) {
                localStorage.setItem('otpLastSentTime', Date.now().toString());
                setResendMessage('Код повторно отправлен');
                setResendTimer(60); setCanResend(false);
            } else setResendMessage('Ошибка при отправке кода');
        } catch { setResendMessage('Ошибка сети. Попробуйте позже.'); }
        finally { setIsResending(false); }
    };

    return (
        <div className={styles.loginContainer}>
            <div className={styles.loginBox}>
                <div className={styles.logoSection}>
                    <div className={styles.logoContainer}>
                        <img src="/logo.png" alt="CONNEXTA SYSTEM" className={styles.logoImage} />
                    </div>
                </div>

                <p className={styles.subtitle} style={{ marginBottom: '1.5rem' }}>
                    Код отправлен в Telegram.<br />Введите его ниже:
                </p>

                {errorMessage && <div className={styles.alert}>{errorMessage}</div>}
                {resendMessage && (
                    <div className={styles.successMessage}>
                        {resendMessage}
                    </div>
                )}

                <div className={styles.loginForm}>
                    <div className={styles.inputWrapper} style={{ textAlign: 'center' }}>
                        <input
                            type="text"
                            value={otp}
                            onChange={e => { if (/^\d*$/.test(e.target.value)) setOtp(e.target.value); }}
                            className={`${styles.input} ${styles.otpInput}`}
                            placeholder="• • • • • •"
                            inputMode="numeric"
                            pattern="\d*"
                            maxLength={6}
                        />
                    </div>

                    {canResend ? (
                        <button
                            onClick={handleResend}
                            disabled={isResending}
                            className={styles.loginButton}
                            style={{ background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)' }}
                        >
                            {isResending
                                ? <div className={styles.loadingSpinner} />
                                : 'Отправить код повторно'
                            }
                        </button>
                    ) : (
                        <p className={styles.mutedText}>
                            Повторно через <strong style={{ color: '#0f766e' }}>{resendTimer}</strong> сек.
                        </p>
                    )}

                    <button
                        onClick={logoutUser}
                        className={styles.secondaryButton}
                    >
                        <LogOut style={{ width: 16, height: 16 }} />
                        Выйти
                    </button>
                </div>

                <p className={styles.mutedText} style={{ marginTop: '1.25rem', fontSize: '0.8rem' }}>
                    Не приходит код?{' '}
                    <a href="https://t.me/SecureAuthHelperBot" target="_blank" rel="noopener noreferrer" className={styles.linkText}>
                        SecureAuthBot
                    </a>
                </p>
            </div>
        </div>
    );
}

export default OTPVerification;
